import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Check, Loader2, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PLANS, UNLIMITED_NOTE } from "../lib/plans";

export default function PricingModal() {
  const { pricingOpen, setPricingOpen, requestCheckout, pricingReason } = useAuth();
  const [busyKey, setBusyKey] = useState(null);

  const buy = async (key) => {
    setBusyKey(key);
    try {
      await requestCheckout(key);
      setBusyKey(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusyKey(null);
    }
  };

  return (
    <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
      <DialogContent className="rounded-none bg-[#0b0b0b] border border-zinc-800 text-white max-w-4xl p-0 gap-0 font-mono max-h-[92vh] overflow-y-auto">
        <div className="p-5 sm:p-8">
          <div className="mb-2 text-[11px] tracking-[0.3em] uppercase text-volt">Go Unlimited</div>
          <DialogTitle className="font-display text-4xl tracking-tight mb-2 font-normal">
            UNLIMITED SPEECH
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-400 mb-4 max-w-xl">
            {pricingReason ||
              "You've hit your free character limit. Pick any plan below to keep generating."}
          </DialogDescription>

          <div className="flex items-start gap-2 mb-8 border border-volt/30 bg-volt/5 px-4 py-3">
            <InfinityIcon size={16} className="text-volt mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-300 leading-relaxed">
              <span className="text-volt font-bold">Same unlimited power on every plan.</span>{" "}
              {UNLIMITED_NOTE}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map((p) => {
              const Icon = p.icon;
              const featured = p.featured;
              return (
                <div
                  key={p.key}
                  data-testid={`plan-${p.key}`}
                  className={`relative flex flex-col p-6 border ${
                    featured
                      ? "border-volt bg-[#101400] md:scale-105 md:-my-1 shadow-[0_0_40px_rgba(212,255,0,0.15)]"
                      : "border-zinc-800 bg-[#0d0d0d]"
                  }`}
                >
                  {p.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-volt text-black text-[10px] font-bold tracking-widest px-3 py-1 whitespace-nowrap">
                      {p.badge}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-4">
                    <Icon size={18} className={featured ? "text-volt" : "text-zinc-400"} strokeWidth={2.5} />
                    <span className="text-sm uppercase tracking-widest text-zinc-300">{p.name}</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className={`font-display tracking-tight ${featured ? "text-5xl text-volt" : "text-4xl text-white"}`}>
                      {p.price}
                    </span>
                    <span className="text-xs text-zinc-500">{p.per}</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-volt mb-5">{p.duration}</div>
                  <ul className="space-y-2 mb-6 flex-grow">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2 text-xs text-zinc-400">
                        <Check size={14} className={featured ? "text-volt mt-0.5" : "text-zinc-500 mt-0.5"} />
                        {perk}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => buy(p.key)}
                    disabled={!!busyKey}
                    data-testid={`buy-${p.key}`}
                    className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-bold uppercase tracking-widest transition-colors duration-100 disabled:opacity-60 ${
                      featured
                        ? "bg-volt text-black hover:bg-volt-hover"
                        : "bg-transparent border border-zinc-700 text-white hover:bg-zinc-800"
                    }`}
                  >
                    {busyKey === p.key && <Loader2 size={15} className="animate-spin" />}
                    {p.cta}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-zinc-500 mt-6 text-center leading-relaxed tracking-wide">
            Secure checkout by Stripe. All paid plans renew automatically until canceled.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
